import { MDBBtn, MDBCol, MDBContainer, MDBRow } from "mdbreact";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Config } from "../../../config/Config";
import { AppContainer } from "../../../containers/App";
import { CampaignContainer } from "../../../containers/Campaign";
import { ErrorContainer } from "../../../containers/Error";
import { StampCardContainer } from "../../../containers/StampCard";
import { Stamp } from "../../../containers/Stamps";
import { UserContainer } from "../../../containers/User";
import formatDate from "../../../utilities/DateFormatter";
import CampaignImage from "../../elements/campaign-image/CampaignImage";
import StampCard from "../../modules/stamp-card/StampCard";
import TenantDescriptionModal from "../../modules/tenant-description-modal/TenantDescriptionModal";
import TextDecoration from "../../modules/text-decoration/TextDecoration";
import classes from "./EntryPage.module.scss";

/**
 * エントリー画面コンポーネント
 */
const EntryPage: React.FC = () => {
  // テナント詳細モーダルの展開状態
  const [isTenantDescriptionOpen, setIsTenantDescriptionOpen] =
    useState<boolean>(false);
  // クリックされたスタンプ
  const [selectedStamp, setSelectedStamp] = useState<Stamp>();

  const history = useHistory();
  const appContainer = AppContainer.useContainer();
  const errorContainer = ErrorContainer.useContainer();
  const userContainer = UserContainer.useContainer();
  const campaignContainer = CampaignContainer.useContainer();
  const stampCardContainer = StampCardContainer.useContainer();

  const user = userContainer.user;
  const userId = user.id || "";

  const campaign = campaignContainer.campaign;
  const campaignId = Config.campaignId || "";

  const stampCard = stampCardContainer.stampCard;
  const stamps = stampCard?.stamps;

  const dateFormat = "YYYY年MM月DD日(AA)";
  const campaignStartDate = formatDate(dateFormat, campaign?.from);

  useEffect(() => {
    if (!user.token) {
      // 未ログインの場合、ログインページに遷移
      history.push("/login");
    }

    if (!stampCard) {
      return;
    }

    if (stampCard.isEntry) {
      // エントリー済みであれば、スタンプカードページに遷移
      history.push("/stamp-card");
    }
  }, [history, stampCard, campaignId, user.token]);

  /**
   * スタンプシートのスタンプをクリックしたときの処理
   * @param id スタンプID
   */
  const handleStampClick = (id: string) => {
    const stamp = stamps?.find((s) => s.id === id);
    if (!stamp) {
      return;
    }

    setSelectedStamp(stamp);
    setIsTenantDescriptionOpen(true);
  };

  /**
   * カスタマイズボタン押下時の処理
   */
  const handleCustomizeClick = () => {
    history.push("/tenant-select");
  };

  /**
   * 「このまま始める」ボタンクリックイベント
   */
  const handleEntryClick = async () => {
    // エントリー状態の更新
    if (!campaign) {
      return;
    }

    try {
      appContainer.setLoading(true);
      const result = await stampCardContainer.entry(campaign.from, campaign.to);
      if (result === "preparing") {
        // キャンペーン開始前の場合
        errorContainer.setCustonError(
          "キャンペーン期間外",
          [
            `本キャンペーンは ${campaignStartDate}`,
            "より開始します。",
            "キャンペーンの開始までお待ちください。",
          ],
          userId
        );
        return;
      }

      if (result === "end") {
        // キャンペーン終了後の場合
        errorContainer.setCustonError(
          "キャンペーン期間外",
          [
            "本キャンペーンは終了しました。",
            "次回のキャンペーンをお待ちください。",
          ],
          userId
        );
        return;
      }
    } catch (error) {
      errorContainer.setDefaultError(userId, error);
    } finally {
      appContainer.setLoading(false);
    }
  };

  return (
    <MDBContainer>
      <CampaignImage className="mt-3" image={campaign?.image || ""} />

      {campaign ? (
        <React.Fragment>
          <div className={`${classes.campaignDescription} py-4 text-center`}>
            <TextDecoration sentences={campaign?.description} />

            <div className={`mt-3 ${classes.externalLink}`}>
              スタンプラリーの詳細は
              <a
                href={campaign?.detail}
                className="mx-1"
                target="_blank"
                rel="noopener noreferrer"
              >
                こちら
              </a>
            </div>
          </div>

          <hr className={classes.hLine}></hr>

          {campaign.status === "active" ? (
            <React.Fragment>
              <div className={`text-center mt-4 ${classes.stepMessage}`}>
                <div className={`${classes.title}`}>STEP2</div>
                <div className={classes.text}>
                  あなただけのスタンプシートを作りましょう
                </div>
              </div>

              {stampCard?.stamps ? (
                <div className="d-flex justify-content-center py-3">
                  <div className={`px-sm-4 ${classes.widthControl}`}>
                    <StampCard
                      title="あなたへおすすめのショップ"
                      stamps={
                        stamps?.map((s) => {
                          return {
                            id: s.id,
                            image: s.image,
                            categoryName:
                              campaign.categories.find(
                                (c) => c.category === s.category
                              )?.label || "",
                            isChecked: false,
                            isAchieve: false,
                          };
                        }) || []
                      }
                      isCustomizable={false}
                      onStampClick={(id) => handleStampClick(id)}
                    ></StampCard>
                  </div>
                </div>
              ) : (
                ""
              )}

              <div className={`mt-2 text-center ${classes.customizeMessage}`}>
                <div>下の「ほかのショップをえらぶ」ボタンより、</div>
                <div>ほかのおすすめショップを選ぶことも可能です</div>
              </div>

              <div className="d-flex justify-content-center">
                <MDBRow
                  className={`${classes.buttonContainer} ${classes.widthControl} mt-3 px-sm-4 justify-content-center`}
                >
                  <MDBCol
                    size="12"
                    md="6"
                    className="mb-3 mb-md-0 px-md-1 d-flex justify-content-center"
                  >
                    <MDBBtn
                      className={`${classes.customButton} my-0 mx-0 py-3`}
                      color="yellow"
                      onClick={handleCustomizeClick}
                    >
                      ほかのショップをえらぶ
                    </MDBBtn>
                  </MDBCol>
                  <MDBCol
                    size="12"
                    md="6"
                    className="px-md-1 d-flex justify-content-center"
                  >
                    <MDBBtn
                      className={`${classes.entryButton} my-0 mx-0 py-3`}
                      color="yellow"
                      onClick={handleEntryClick}
                    >
                      このショップではじめる！
                    </MDBBtn>
                  </MDBCol>
                </MDBRow>
              </div>
            </React.Fragment>
          ) : (
            <div
              className={`text-center alert alert-danger mx-3 mt-5 py-3 ${classes.alert}`}
            >
              {campaign.status === "preparing" ? (
                <React.Fragment>
                  本キャンペーンは<br></br>
                  <span className="mx-1">{campaignStartDate}</span>
                  より開始します。<br></br>
                  キャンペーンの開始までお待ちください。
                </React.Fragment>
              ) : (
                <React.Fragment>
                  本キャンペーンは終了しました。<br></br>
                  またのご参加をお待ちしています。
                </React.Fragment>
              )}
            </div>
          )}
        </React.Fragment>
      ) : null}

      {/* テナント詳細モーダル */}
      <TenantDescriptionModal
        isOpenModal={isTenantDescriptionOpen}
        isActionButtonHidden
        onStampSelect={() => {}}
        onCloseButtonClick={() => {
          setIsTenantDescriptionOpen(false);
        }}
        tenantDescription={
          selectedStamp
            ? {
                image: selectedStamp.banner || selectedStamp.image,
                name: selectedStamp.name,
                description: selectedStamp.description || "",
                isRecommended: true,
                genre: selectedStamp.genre,
                floor: selectedStamp.area?.join("/"),
                businessHours: selectedStamp.businessHours,
                tel: selectedStamp.tel,
                budget: selectedStamp.budget,
              }
            : undefined
        }
      />
    </MDBContainer>
  );
};

export default EntryPage;
